import React, { useState, useEffect } from "react"
import { DateTime } from "luxon"
import { SignInLocationReplace } from "./SignIn"
import { useTranslation } from "react-i18next"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faSmile, faInfoCircle, faExternalLink, faDownload, faQuestionCircle } from "@fortawesome/pro-regular-svg-icons"
import { faQuestionCircle as faQuestionSolid, faInfoCircle as faInfoSolid } from "@fortawesome/pro-solid-svg-icons"
import { sprintf } from "sprintf-js"

const { document: { location: { hostname } } } = window,
  [firstSubdomain] = hostname.split("."),
  isLive = firstSubdomain === "www",
  isDev23 = firstSubdomain === "dev23",
  isLocalhost = hostname === "localhost",
  isLatest = firstSubdomain === "latest",
  enableMessageBanking = !isLive

// ----

const { apiServer, isBrowser } = require("./_util"),
  payPrepURL = apiServer + "/payPrep/",
  fetchParams = {
    headers: { "Content-Type": "application/json" },
    crossDomain: true
  }

export const HomePage = () => {
  const loginToken = localStorage.getItem("token"),
    [myDetails, setMyDetails] = useState({}),
    [showHCPHelp, setShowHCPHelp] = useState(false),
    { t } = useTranslation("translation")

  useEffect(() => {
    const whoAmI = async () => {
      const response = await fetch(`${apiServer}/whoami${loginToken}`, fetchParams),
        result = await response.json(),
        newDetails = { ...myDetails, ...result }

      console.info(`Setting details to`, newDetails)

      setMyDetails(newDetails)
    }
    whoAmI()
  }, [])

  if (!loginToken) return SignInLocationReplace()
  // if (window.document.location.hostname.match(/^(?:latest|dev23)\./)) console.info(myDetails);

  const { product, voiceDesignSubmitted, savedFiles, regionalAccent, showLinkToUploadFiles,
    pur, firstName, lastName, email, bas, jobRole, tags, enableClinicianInterface, patsLinks,
    pats, dateOfBirth } = myDetails,
    hasCompletedRecording = !!(myDetails.hasCompletedRecording === "Yes"),
    hasConfirmedRecording = !!(myDetails.hasConfirmedRecording === "Yes"),
    isPaid = !!(bas && bas.rows && bas.rows.length > 0),
    isVoiceDesign = product === "Voice Design",
    isVoiceDesignSingleVoice = !!(tags && (!!tags.find(t => t === "VoiceDesignSingleVoice"))),
    hasDesignOptionsSet = !!(tags && (!!tags.find(t => ["DesignOptionsSet", "RepairOptionsSet"].includes(t)))),
    isVoiceRepair = product === "Voice Repair",
    productRevoked = !!(pur && pur.rows && pur.rows[0] && pur.rows[0].revoked === "Yes"),
    repairVoiceChosen = !!(pur && pur.rows && pur.rows[0] && pur.rows[0].repairVoiceChosen),
    isVoiceRepairChosenAndPaid = isVoiceRepair && repairVoiceChosen && isPaid,
    isVoiceDesignChosenAndPaid = isVoiceDesign && repairVoiceChosen && isPaid,
    isVoiceBuild = product === "Voice Build",
    isVoiceSave = product === "Voice Save",
    isVolunteer = !!(product === "Volunteer"),
    isDonor = !!(product === "Donor"),
    numUploadedFiles = !!savedFiles ? savedFiles.length : 0,
    numApprovedFiles = !!numUploadedFiles ? savedFiles.filter(f => f.approved === "Yes").length : 0,
    numReviewingFiles = !!numUploadedFiles ? savedFiles.filter(f => f.reviewed === "No").length : 0,
    fundingRelevant = tags && !!tags.find(t => t.match(/^Funding/)),
    fundingApproved = tags && !!tags.find(t => t.match(/^Funding (?:Application )?Approved$/)),
    voiceAvailable = tags && !!tags.find(t => t.match(/(?:VoiceApproved|VoiceAlerted)/)),
    isClinician = !!jobRole,
    displayLogoutLink = !!(isBrowser.safari),
    displayContinueButton = false,
    voiceDesignLinkToRecorder = <div className="is-size-5 light" style={{ marginTop: "20px" }}>
      To access our online voice recorder <a
        className="orange hinted"
        href={`/speechvault/start/${loginToken}`}>
        click here</a>.</div>,
    linkToFileUploads = <div className="is-size-5 light" style={{ marginTop: "20px" }}>
      To upload files to us <a
        className="orange hinted"
        href={`/speechvault/upload/${loginToken}`}>
        click here</a>.</div>

  localStorage.setItem('product', product)

  // console.info()

  const goToPaymentPage = e => {
    if (e) e.preventDefault()
    fetch(payPrepURL, {
      ...fetchParams,
      method: "POST",
      body: JSON.stringify({ token: loginToken })
    })
      .then(result => result.ok ? result.json() : {})
      .then(data => {
        if (!data.ok)
          return window.alert("Sorry, an error occurred. Please contact us for assistance.")

        localStorage.setItem("product", data.product)
        localStorage.setItem("price", data.price)
        localStorage.setItem("sku", data.sku)
        localStorage.setItem("uid", data.uid)
        localStorage.setItem("stripeSessionId", data.stripeSessionId)
        window.document.location.assign("/speechvault/checkout/" + loginToken)
      })
    return false
  }

  const getStatus = () => {

    if (productRevoked) return `This voice has been refunded.` // revoked isn't quite refunded, but it's close :-|

    if (!product) return <div>
      Please <a href={`${process.env.PUBLIC_URL}/service`} className="orange hinted"
      >select one of our voice bank services</a> to complete your registration.
    </div>

    if (fundingRelevant && !fundingApproved && hasCompletedRecording) {
      const [charityTag] = tags.filter(t => t.match(/^Charity/)),
        whichCharity = charityTag ? charityTag.replace(/^Charity/, "") : ''

      return <span>
        Your application for funding {whichCharity ? 'from ' + whichCharity : ''} has been received and is in progress. You will receive an email when your request has been approved. <a
          className="orange hinted"
          href={"/speechvault/listen/" + loginToken}
        // href={"/speechvault/listen/"}
        >Listen to samples</a>
      </span>
      return `Your application for funding ${whichCharity ? 'from ' + whichCharity : ''} has been received and is in progress. You will receive an email when your request has been approved.`
    }

    if (isPaid && (
      (!isVoiceRepair || repairVoiceChosen) &&
      !isVoiceSave &&
      (!isVoiceDesign || isVoiceDesignChosenAndPaid))) { // they can download their voice. they might also be able to re-record their voice if it's been forced back to not-completed.
      const reRecord = hasCompletedRecording ? null : <a
        className="orange hinted"
        href={`/speechvault/review/${loginToken}`}>Record more</a>
      return <span><a
        className="orange hinted"
        href={`/speechvault/download/${loginToken}`}>
        Download your voice
      </a>. {reRecord}</span>
    }

    if (!isVoiceDesign && !hasCompletedRecording) {

      return <span>Please{" "}
        {myDetails.preferredScript || myDetails.numSamples ? <a className="orange hinted"
          href={"/speechvault/review/" + loginToken}>complete your recordings</a> :
          <span><a className="orange hinted"
            href={"/speechvault/book/" + loginToken}>choose a book</a> and{" "}
            <a className="orange hinted"
              href={"/speechvault/review/" + loginToken}>start your recordings</a>
          </span>}
      </span>
    }

    if (!dateOfBirth) return <div>
      Please <a href={`${process.env.PUBLIC_URL}/mc`} className="orange hinted"
      >continue creating your account</a>.
    </div>

    if (!regionalAccent) return <div>
      Please <a href={`${process.env.PUBLIC_URL}/accent`} className="orange hinted"
      >select your accent</a> to complete your account details.
    </div>

    if (isVoiceSave && !isPaid)
      // voice save non-clinician unpaid people have to pay before doing anything else
      return <span>Please <a
        className="orange hinted"
        href="/" onClick={goToPaymentPage}>
        complete your payment
      </a> for your Voice Save so that we can begin recording.</span>

    const listenToVoiceSamplesSpan = <span>
      <a href={"/speechvault/listen/" + loginToken}
        className="orange hinted">Listen to your voice samples</a> to continue.
    </span>,
      accessOurVoiceDesignOptionsSpan = <span>
        <a href={`/session/design`} className="orange hinted">Access our Voice Design options</a>
      </span>,
      goToVoiceDesignSpan = <span>Please <a href="design" className="orange hinted"
      >complete your Voice Design choices</a>.<br /><br />
      </span>,
      statusForVoiceDesignRouteUpload = <span>Ready for audio file uploads</span>,
      statusForVoiceDesignAudioReviewing = <span>Thank you for submitting your audio to our team.
        We are reviewing your audio now. A member of our team will be in touch to confirm if
        we have sufficient audio.
      </span>,
      statusForVoiceDesignAudioApproved = <span>
        We have approved the audio you have submitted. Your synthetic voice is being generated.
      </span>,
      statusForVoiceDesignAudioDenied = <span>
        Sorry, your uploaded audio was not approved for synthetic voice generation. Please try again.
      </span>,
      isVoiceDesignRouteUpload = !!(myDetails && myDetails.voiceDesignRoute === "Upload"),
      isVoiceDesignAudioInReview = !!(savedFiles &&
        savedFiles.filter(f => (!f.approved) && f.reviewed === "No").length),
      isVoiceDesignAudioApproved = !!(savedFiles &&
        savedFiles.filter(f => f.approved === "Yes").length),
      isVoiceDesignAudioDenied = !!(savedFiles &&
        savedFiles.filter(f => f.approved === "No").length),
      voiceDesignInProgress = isVoiceDesignRouteUpload ?
        (
          isVoiceDesignAudioApproved ? statusForVoiceDesignAudioApproved :
            (isVoiceDesignAudioInReview ? statusForVoiceDesignAudioReviewing :
              (isVoiceDesignAudioDenied ? <span>{statusForVoiceDesignAudioDenied} {statusForVoiceDesignRouteUpload}</span> :
                statusForVoiceDesignRouteUpload
              )
            )
        ) :
        <span>
          <a href="donors" className="orange hinted">View voice donor information</a><br />
          <a href={`/speechvault/upload/${encodeURIComponent(loginToken)}`} className="orange hinted">Upload audio files</a><br />
          {accessOurVoiceDesignOptionsSpan}
        </span>,
      voiceDesignStatus = hasDesignOptionsSet ? listenToVoiceSamplesSpan :
        voiceDesignSubmitted === "Yes" ?
          (isVoiceDesignSingleVoice ? listenToVoiceSamplesSpan : voiceDesignInProgress) : goToVoiceDesignSpan,
      makeStatus = () => {
        if (isVoiceDesign || hasDesignOptionsSet) return voiceDesignStatus

        if (["Voice Build", "Voice Repair"].includes(product) && !isPaid && !voiceAvailable && !repairVoiceChosen && hasCompletedRecording && !hasConfirmedRecording)
          return <span>
            You need to confirm the information we have about your recordings is correct before we can generate your voice. Please <a href="/session/confirm" className="orange hinted" style={{
              fontWeight: 500
            }}>confirm your voice details</a> and once you have confirmed this information, we will start generating your synthetic voice.
          </span>

        return
      },
      statusGeneral = makeStatus() ||
        (isVoiceRepair ?
          (pur && pur.rows && pur.rows[0] && pur.rows[0].repairVoiceTagSet && !repairVoiceChosen) ? <span>
            <a href={`/speechvault/listen/${loginToken}`}
              className="orange hinted">Listen to your voice samples</a> to continue.
          </span> :
            (parseInt(pur.count) ?
              (isVoiceRepairChosenAndPaid ?
                <span>
                  {isPaid ?
                    <span>Thank you for purchasing your SpeakUnique voice.{" "}
                      <a
                        className="orange hinted"
                        href={"/speechvault/download/" + loginToken}>
                        Download your voice
                      </a>.</span> :
                    <span>
                      Ready for download. <a
                        className="orange hinted"
                        href="/" onClick={goToPaymentPage}>
                        Pay now to continue
                      </a>.</span>}
                </span> :
                (pur.rows[0].repairVoiceTagSet ?
                  <span>
                    Your Voice Repair has been generated.
                    <div className="has-text-centered">
                      <button className="button buttonPurple buttonBottomCerise"
                        onClick={() => { window.location.assign("/speechvault/listen/" + loginToken) }}>
                        Listen to samples
                      </button>
                    </div>
                  </span> :
                  (isVolunteer ?
                    <span>Thank you for donating your voice.</span> :
                    <span>Your recordings have been submitted. You will be notified by email when your synthetic voice is ready to listen to. If you do not receive an email, please check your junk folder or check back here for any updates.</span>
                  )
                )
              ) :
              <span>
                {/* Voice Repair.&nbsp; */}
                {!voiceAvailable ?
                  <span>You will be notified by email when your synthetic voice is ready to listen to. If you do not receive an email, please check your junk folder or check back here for any updates.</span> :
                  hasCompletedRecording ?
                    (
                      (fundingApproved ?
                        (voiceAvailable ?
                          <span>
                            Your product funding has been approved! <a href={"/speechvault/download/" + loginToken}
                              className="orange hinted">Download your voice</a>
                          </span> :
                          <span>
                            Your product funding has been approved! Check back soon to download your voice.
                          </span>
                        ) :
                        (fundingRelevant ?
                          <span>Your funding application has been submitted.</span> :
                          <span>Please <a
                            className="orange hinted"
                            href="/" onClick={goToPaymentPage}>
                            complete your payment
                          </a>.</span>)
                      )
                    ) : (<span>
                      Please <a href={"/speechvault/review/" + loginToken}
                        className="orange hinted">complete your voice recordings</a> so we can begin work on your Voice Repair.
                    </span>)
                }
              </span>
            ) :
          (isDonor ? <span>
            Complete. <FontAwesomeIcon icon={faSmile}
              className="orange" size="1x"
            /> Thank you!
          </span> : <span>
            {
              isVoiceSave ? <span>Your {isClinician ? 'synthetic voice' : 'Voice Save'} has been generated.</span> :
                isPaid ?
                  <span>Thank you for purchasing your SpeakUnique voice.</span> :
                  (isVolunteer ? <span>Thank you for donating your voice.</span> : null)
              // <span>Your {product} has been generated.</span>)
            }
            {voiceAvailable ?
              <h4 className="title is-4 has-text-centered" style={{ marginTop: 14 }}>
                <a className="orange hinted"
                  href={"/speechvault/listen/" + loginToken}>
                  {(isVoiceSave || isClinician) ? "Listen to samples" :
                    "Download/Listen to your synthetic voice"
                  }
                </a>
              </h4> : <span>You will be notified by email when your synthetic voice is ready to listen to. If you do not receive an email, please check your junk folder or check back here for any updates.</span>
            }
          </span>)
        )

    return <div style={{ paddingLeft: 20 }}>
      {statusGeneral}
      {myDetails.uFiles === "Yes" ? linkToFileUploads : null}
    </div>
  },
    status = getStatus()

  // return <div className="has-text-centered">This page is coming back soon, please bear with us...</div>

  return !email ?
    <div className="has-text-centered" style={{ color: '#eee' }}>Loading...<br /><br />
    </div> :
    <div className="container">
      <div className="columns is-vcentered is-gapless">
        <div className="column is-two-thirds" style={{ paddingLeft: 14 }}>
          <h1 className="title is-1">
            {firstName ? `${firstName} ${lastName}` :
              ""
            } {isClinician ? <span style={{
              color: "#bbb", marginLeft: 70,
              fontFamily: "JAF Domus,Quicksand,sans-serif",
              fontSize: "15pt"
            }}>Clinician Account</span> : null}
          </h1>
        </div>
        <div className="column is-one-third has-text-right">
          <h4 className="title is-4 light" >
            &lt;{email}&gt;
            {displayLogoutLink ?
              <span style={{ marginLeft: 20 }}><a href={"/speechvault/lo"}
                style={{ fontSize: '14pt' }}
                className="orange hinted"
              >Logout</a></span> : null}
          </h4>
        </div>
      </div>

      <div className="columns">
        <div
          className="column bigBoxes"
          style={{ padding: 57, paddingTop: 11, paddingBottom: 21 }}
        >
          <h3 id="payTitle" className="title is-3 has-text-centered orange">
            {product && product === `Volunteer` ? `Voice Volunteer` :
              `Your SpeakUnique Products`}
          </h3>
          {product && product === `Volunteer` ?
            <div>
              {myDetails && myDetails.lng && myDetails.lng.length ?
                myDetails.lng.map((l, idx) => {
                  const maxRec = myDetails.maxSentencesPerLanguage ?
                    myDetails.maxSentencesPerLanguage.find(e => e["lng"] === l.language) : null,
                    max = maxRec ? parseInt(maxRec["maxSentenceNumber"]) : null,
                    bookChosen = !!l.book,
                    href = (!bookChosen) ? "/we/tips" : "/we/prepare" // "/record-my-voice.html";

                  return <div key={`lng${idx + 1}`} className="is-size-5">
                    {t(l.language)}:{" "}
                    {max && max >= 299 ? t("Complete") : <a href={href}
                      onClick={e => {
                        localStorage.setItem("recordingLanguage", l.language)
                        localStorage.removeItem("currentIdx")
                      }}
                      className="orange hinted">
                      {t("In progress")}</a>}
                  </div>
                }) : null}
              <div className="is-size-5" style={{ marginTop: 17 }}>
                <a href="/we/select" className="orange hinted">
                  Add{" "}
                  {myDetails && myDetails.lng ?
                    (myDetails.lng.length === 0 ? `your first` : `another`)
                    : null}{" "}
                  language...
                </a>
                {/* {myDetails && myDetails.lng && myDetails.lng.length === 0 ?
                  <a href="/we/select">
                    <sup
                      style={{
                        backgroundColor: "lightyellow",
                        color: "black",
                        marginLeft: 8,
                        paddingLeft: 10,
                        paddingRight: 10,
                        fontSize: "small",
                        fontWeight: "bold"
                      }}
                    ><FontAwesomeIcon icon={faLongArrowLeft}
                      style={{
                        marginRight: 6
                      }}
                      />
                      START HERE!</sup></a>
                  : null} */}
                {/* <pre>
                  {JSON.stringify(myDetails, null, 5)}
                </pre> */}
              </div>

            </div> :
            <h4 className="title is-4 light">
              {!!product ? 'Product: ' : ''} {product || <a
                className="orange hinted"
                href={"/speechvault/voice/" + loginToken}
              >
                Complete your registration
              </a>}{" "}
              <br />
              {!!product && !(myDetails.voiceDesignEnableRecorder === "Yes") ?
                'Status: ' : ""}{!!product ? status : ''}
              {myDetails.voiceDesignEnableRecorder === "Yes" && voiceDesignLinkToRecorder}
              {
                (!!numUploadedFiles || showLinkToUploadFiles === "Yes" || myDetails.voiceDesignRoute === "Upload") ?
                  <div style={{ paddingTop: '20px', paddingLeft: '20px' }}>
                    {
                      !!numApprovedFiles ?
                        <div>
                          We have approved the audio you have submitted. Your synthetic voice is being generated.
                        </div> :
                        (!!numReviewingFiles ?
                          <div>Your audio files are being reviewed.</div> : null)
                    }
                    {(!!numUploadedFiles || showLinkToUploadFiles === "Yes" || myDetails.voiceDesignRoute === "Upload") ?
                      <div>
                        {!!numUploadedFiles ? <span>You have {numUploadedFiles} uploaded file{
                          numUploadedFiles === 1 ? "" : "s"}. </span> : null}
                        <a href={"/speechvault/upload/" + loginToken}
                          className="orange hinted">
                          {(!!numApprovedFiles || !!numReviewingFiles) ? 'Access uploaded audio files' : 'Upload audio files'}
                        </a>
                      </div> : null}
                  </div> : null
              }
            </h4>}
          {!!product && displayContinueButton &&
            <div className="has-text-centered">
              <input
                type="submit"
                className="button buttonPurple buttonBottomCerise"
                value="Continue >"
                onClick={() => window.location.assign("/speechvault/review/" + loginToken)}
              />
            </div>}

          {(myDetails && (myDetails.enableRecordingsDownload === "Yes")) ?
            <div>
              <a href="./" className="title is-4 light orange"
                onClick={e => {
                  e.preventDefault()
                  const url = `/zapi/t${new Date().valueOf()}/dZip${loginToken}/` +
                    "SpeakUniqueRecordings.zip" // the user sees the final name on their filesystem
                  // window.alert(`Would open ${url}`);
                  // return;
                  window.open(url)
                  return
                }}
              >
                Download your recordings
                <FontAwesomeIcon icon={faDownload}
                  style={{
                    marginLeft: 12
                  }}
                />
              </a>
            </div> : null}

          {
            enableMessageBanking ?
              <div className="has-text-centered"
                style={{
                  marginTop: 100,
                  fontSize: "16pt"
                }}>
                <strong>Message Banking:
                  <a href="/session/message-bank"
                    style={{ marginLeft: 8 }}
                    className="cerise">Go to your message banking</a></strong>
              </div> : null}

        </div>
        <div
          className="column bigBoxes"
          style={{ padding: 57, paddingTop: 11, paddingBottom: 21, borderWidth: 0 }}
        >
          <form action={"/speechvault/userDetails/" + loginToken}>
            <h3 id="accentSettingsTitle" className="title is-3 orange has-text-centered ">
              Your Details &amp; Preferences
            </h3>
            <div className="is-size-5 light" style={{ marginBottom: 6 }}>
              Name: {myDetails.firstName ? `${myDetails.firstName} ${myDetails.lastName}` : ""}
            </div>
            <div className="is-size-5 light" style={{ marginBottom: 6 }}>
              Email: <span className="monospaced">{myDetails.email || ""}</span>
            </div>
            <div className="is-size-5 light" style={{ marginBottom: 6 }}>
              Date of Birth: {myDetails.dateOfBirth ?
                sprintf("%d %s %d", DateTime.fromISO(myDetails.dateOfBirth).day,
                  DateTime.fromISO(myDetails.dateOfBirth).monthLong,
                  DateTime.fromISO(myDetails.dateOfBirth).year,
                ) :
                // DateTime.fromISO(myDetails.dateOfBirth).toLocaleString() :
                ""}
            </div>
            <div className="is-size-5 light" style={{ marginBottom: 6 }}>
              Marketing Information: {myDetails.marketingOptIn || ""}
            </div>
            <div className="is-size-5 light" style={{ marginBottom: 6 }}>
              {["Voice Design"].includes(myDetails.product) ? null :
                <>
                  Recorder: {`${myDetails.useLoFiRecorder}` === "Yes" ? "Basic" : "Normal"}
                </>}
            </div>
            <div className="has-text-centered">
              <input
                type="submit"
                className="button buttonPurple buttonBottomCerise"
                value="Update"
              />
            </div>
          </form>
        </div>
      </div>


      {// window.document.location.hostname.match(/^(?:(?:dev23|latest)\.|localhost)/) ? // second row of preferences boxes, WEB-405 clinician/hcp
        true ? // WEB-405 go live 01/11/2023
          (isVolunteer ? null :
            <div className="columns">
              <div
                className="column bigBoxes"
                style={{ padding: 57, paddingTop: 11, paddingBottom: 21 }}
              >
                <h3 id="payTitle" className="title is-3 has-text-centered orange">Your Voice Details</h3>
                <div style={{ minHeight: 101 }}>
                  <h4 className="title is-4 light">
                    Accent country: {myDetails ? myDetails.regionalAccent || "?" : null}<br />
                    Accent region: {myDetails ? myDetails.accentInfluence || "?" : null}<br />
                    {myDetails && myDetails.secondaryInfluence ?
                      <>Additional information: {myDetails.secondaryInfluence || "?"}<br /></> : null}
                  </h4>
                </div>
                <div className="has-text-centered">
                  <button className="button buttonPurple buttonBottomCerise"
                    onClick={e => {
                      e.preventDefault()
                      window.document.location.pathname = '/speechvault/userAccent/' + loginToken
                    }}
                    disabled={!(myDetails &&
                      ['Voice Build', 'Voice Repair', 'Voice Design', 'Donor'].includes(myDetails.product) &&
                      !(myDetails.hasCompletedRecording === "Yes"))}>
                    Update
                  </button>
                </div>
              </div>
              <div className="column bigBoxes"
                style={{
                  padding: 57, paddingTop: 11, paddingBottom: 21, borderWidth: 0,
                  opacity: myDetails &&
                    (
                      ['Voice Build', 'Voice Repair', 'Voice Design'].includes(myDetails.product)
                    ) ? null : 0
                }}>
                {isDonor ? null :
                  (isClinician ? <div>
                    {enableClinicianInterface === "Yes" ? <div>
                      <h3 className="title is-3 has-text-centered orange">
                        Your Clinician Account <FontAwesomeIcon
                          icon={
                            showHCPHelp ? faInfoSolid : faInfoCircle}
                          onClick={e => {
                            e.preventDefault()
                            setShowHCPHelp(!showHCPHelp)
                            // window.confirm(`Welcome to a preview of SpeakUnique Clinician Services. Please contact us with any issues.`);
                            return false
                          }} />
                      </h3>
                      {
                        showHCPHelp ? <div className="is-size-5 light">
                          Welcome to a preview of SpeakUnique Clinician Services.<br />
                          In this section you can manage all of the digital voices for your patients who add your contact details to their SpeakUnique account.<br />
                          <div className="has-text-centered" style={{ marginTop: 9 }}>
                            <button className="button buttonPurple buttonBottomCerise"
                              onClick={e => {
                                e.preventDefault()
                                setShowHCPHelp(false)
                              }}
                            >
                              OK
                            </button>
                          </div>
                        </div> : <div>
                          <div style={{ minHeight: 121 }}>
                            <div className="is-size-5 light" style={{ marginBottom: 6 }}>
                              Your job role: {jobRole}
                              <br />
                              {patsLinks && patsLinks.length && pats && pats.length ?
                                <div>Your patients:</div> :
                                <div>
                                  If your patients add your details to their account, they will be listed here.
                                </div>}
                            </div>
                            {patsLinks && patsLinks.length && pats && pats.length ?
                              <div>
                                <div className="is-size-5 light" style={{ marginBottom: 6 }}>

                                  {pats
                                    .slice(0, 3)
                                    .map((patient, idx) => {
                                      return <div key={`patient${idx + 1}`}
                                        style={{
                                          paddingLeft: 70
                                        }}>
                                        {patient.name}
                                      </div>
                                    })}
                                  {pats.length > 3 ? <div style={{
                                    marginTop: 6,
                                    paddingLeft: 70,
                                    color: "grey", fontSize: "12pt",
                                    fontWeight: 500,
                                  }}>
                                    And {(pats.length - 3).toLocaleString()} more patient{
                                      pats.length === 4 ? "" : "s"
                                    }
                                  </div> : null}
                                </div>
                                <div className="has-text-centered">
                                  <button className="button buttonPurple buttonBottomCerise"
                                    onClick={e => {
                                      e.preventDefault()
                                      window.document.location.pathname = '/session/hcpL'
                                    }}
                                  // disabled={!(myDetails &&
                                  //   ['Voice Build', 'Voice Repair', 'Voice Design', 'Donor'].includes(myDetails.product) &&
                                  //   !(myDetails.hasCompletedRecording === "Yes"))}
                                  >
                                    See More
                                  </button>
                                </div>
                              </div> : <div>
                                <div>
                                </div>
                              </div>}
                          </div>
                        </div>
                      }
                    </div> :
                      <div>
                        <h3 className="title is-3 has-text-centered orange">
                          SpeakUnique for Clinicians
                        </h3>
                        <div className="is-size-5 light" style={{ marginBottom: 6 }}>
                          Thank you for registering with SpeakUnique.<br />
                          Your job role: {jobRole}<br /><br />
                          Contact <a href="mailto:support@speakunique.co.uk" className="orange hinted">support@speakunique.co.uk</a> with any queries.
                        </div>
                      </div>}
                  </div> :
                    <div>
                      <h3 id="payTitle" className="title is-3 has-text-centered orange">Your Healthcare Professional
                        <FontAwesomeIcon icon={
                          showHCPHelp ? faQuestionSolid : faQuestionCircle}
                          onClick={e => {
                            e.preventDefault()
                            setShowHCPHelp(!showHCPHelp)
                          }}
                          style={{ marginLeft: 9 }}
                        />
                      </h3>
                      <div style={{ minHeight: 101 }}>
                        {showHCPHelp ?
                          <h5 className="title is-5 light">
                            We know that many people are supported by a healthcare professional, e.g. speech and language therapist, to bank their voice. We offer healthcare professionals accounts so they can monitor your progress and work with us to ensure the voices meet your needs.
                          </h5> :
                          (myDetails && myDetails.hcp && myDetails.hcp.name ?
                            <h4 className="title is-4 light">
                              Their name: {myDetails && myDetails.hcp ? myDetails.hcp.name || "" : null}<br />
                              Their email: <span style={{ fontSize: "13pt" }}>{myDetails && myDetails.hcp ? myDetails.hcp.email || "" : null}</span><br />
                              Their phone: {myDetails && myDetails.hcp ? myDetails.hcp.phone || "" : null}<br />
                              Their role: {myDetails && myDetails.hcp && (myDetails.hcp.jobRole || myDetails.hcp.role) ?
                                (myDetails.hcp.jobRole || myDetails.hcp.role) :
                                null}<br />
                            </h4> : <h4 className="title is-4 light">
                              If you are being supported with voice banking by your healthcare professional, <a
                                href={'/speechvault/userHCP/' + loginToken}
                                className="orange hinted">add their details here</a>.
                            </h4>
                          )}
                      </div>
                      {myDetails && myDetails.hcp && myDetails.hcp.name ?
                        <div className="has-text-centered">
                          {showHCPHelp ? <button className="button buttonPurple buttonBottomCerise"
                            onClick={e => {
                              e.preventDefault()
                              setShowHCPHelp(false)
                            }}>OK</button> :
                            <button className="button buttonPurple buttonBottomCerise"
                              onClick={e => {
                                e.preventDefault()
                                window.document.location.pathname = '/speechvault/userHCP/' + loginToken
                              }}
                              disabled={!(myDetails &&
                                ['Voice Build', 'Voice Repair', 'Voice Design', 'Donor'].includes(myDetails.product))}>
                              Update
                            </button>}
                        </div> : null}
                    </div>)
                }
              </div>
            </div>) : null}



      <div className="has-text-centered">
        For help contact our great support team at <a
          href="mailto:support@speakunique.co.uk" className="orange hinted">support@speakunique.co.uk</a>
      </div>
    </div>
}
